import { HttpResponse } from '@angular/common/http';

/**
 * Shared blob-download helpers (the single home — Rule 17). Used by the SME statement export
 * (EM-360, C7) and reusable by any other "download a streamed file" surface (W1 V-IBAN Statements
 * tab, document vault, …) so the `Content-Disposition` parsing + object-URL lifecycle is not
 * re-implemented per feature.
 */

/**
 * Trigger a browser download of `blob` as `filename` via a transient object URL.
 *
 * @param blob     the file contents
 * @param filename the suggested download name
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Revoke on the next tick so the click has been dispatched.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * Extract the download filename from a blob `HttpResponse`'s `Content-Disposition` header,
 * preferring the RFC 5987 `filename*` form, falling back to plain `filename`, then to `fallback`.
 *
 * @param response the blob response
 * @param fallback the filename to use when the header is absent / unparseable
 */
export function filenameFromResponse(response: HttpResponse<Blob>, fallback: string): string {
  const header = response.headers.get('Content-Disposition');
  if (!header) {
    return fallback;
  }
  // RFC 5987: filename*=UTF-8''encoded-name
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain?.[1]) {
    return plain[1].trim();
  }
  return fallback;
}
